/**
 * @fileoverview Calculation behind the Sales Performance mock endpoints.
 * @description Works out the figures the backend's Sales Performance routes answer with (Confluence
 * 3.5.11.36-.38), projected on the spot from the shared roster, Target and Tenant mock stores so a
 * target moved through any mock-only action shows up here too. Follows the backend's rules: a
 * target belongs to the period of its completion date, or of its due date while it is not achieved;
 * every target counts as assigned; credit goes to the current assignee; achieved means the bank said
 * yes (Subscription Requested or later) and onboarded means Live. A Super Admin or Sales Head sees
 * the whole sales organisation and may pick a team; a Sales Manager sees their downline and an
 * Executive only themselves.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { resolveDownlineUserIds } from '@/features/sales/lib/sales-hierarchy.utils'

import { displayStatusOf, financialYearOf, indiaToday } from './my-page-mock.service'
import { listSalesRoster } from './sales-roster-mock-store'
import { listTargets } from './targets-mock-store'
import { findTenantById } from './tenants-mock-store'

import type { TargetWithOverdue } from './targets-mock-store'
import type {
  SalesPerformancePeriod,
  SalesPerformancePendingStatus,
  SalesPerformancePeriodCode,
  SalesPerformanceRole,
  SalesPerformanceScope,
} from '@/features/sales/types/sales-performance.types'
import type { SalesUserRecord, Tenant } from '@/features/sales/types/tenant.types'

/** @description The most rows the ranking and the pending list return (the backend's default). */
export const PERFORMANCE_LIST_CAP = 10

const ROLE_CODES: Record<string, SalesPerformanceRole> = {
  'Sales Head': 'SALES_HEAD',
  'Sales Manager': 'SALES_MANAGER',
  'Sales Executive': 'SALES_EXECUTIVE',
}

const ROLE_ORDER: Record<SalesPerformanceRole, number> = {
  SALES_HEAD: 0,
  SALES_MANAGER: 1,
  SALES_EXECUTIVE: 2,
}

const QUARTER_STARTS: Record<string, [number, number]> = {
  Q1: [0, 4],
  Q2: [0, 7],
  Q3: [0, 10],
  Q4: [1, 1],
}

/** @description A target with its bank and the facts the performance figures need. */
export interface PerformanceTarget {
  target: TargetWithOverdue
  tenant: Tenant
  achieved: boolean
  onboarded: boolean
  /** The date the target counts in: its completion date once achieved, else its due date. */
  date: string
}

/**
 * @description Whether a user is a selling person (Head, Manager or Executive) who is active.
 * @param {SalesUserRecord} user - A roster user.
 * @returns {boolean} True for an active Sales Head, Manager or Executive.
 */
export function isSeller(user: SalesUserRecord): boolean {
  return user.status === 'Active' && user.role in ROLE_CODES
}

/**
 * @description A seller's role as the backend names it.
 * @param {SalesUserRecord} user - A selling roster user.
 * @returns {SalesPerformanceRole} The role code.
 */
export function roleCodeOf(user: SalesUserRecord): SalesPerformanceRole {
  return ROLE_CODES[user.role] ?? 'SALES_EXECUTIVE'
}

/**
 * @description What a caller's role lets them see and do on this screen.
 * @param {SalesUserRecord} caller - The signed-in user.
 * @returns {SalesPerformanceScope} The scope kind and what the caller may filter and export.
 */
export function scopeOf(caller: SalesUserRecord): SalesPerformanceScope {
  const wide = caller.role === 'Super Admin' || caller.role === 'Sales Head'
  return {
    kind: wide ? 'ORGANISATION' : caller.role === 'Sales Manager' ? 'TEAM' : 'SELF',
    canFilterTeam: wide,
    canExport: caller.role === 'Super Admin',
  }
}

/**
 * @description The people the caller's scope covers.
 * @param {SalesUserRecord} caller - The signed-in user.
 * @returns {SalesUserRecord[]} The sellers in scope.
 */
export function peopleInScope(caller: SalesUserRecord): SalesUserRecord[] {
  const roster = listSalesRoster()
  const sellers = roster.filter(isSeller)
  if (scopeOf(caller).kind === 'ORGANISATION') return sellers
  const ids = new Set(resolveDownlineUserIds(caller.id, roster))
  return sellers.filter((user) => ids.has(user.id))
}

/**
 * @description The first and last day of a quarter, or of the whole financial year, and whether it
 * has started.
 * @param {SalesPerformancePeriodCode} code - FY, or Q1 to Q4.
 * @param {string} today - Today in India, yyyy-mm-dd.
 * @returns {{ period: SalesPerformancePeriod; started: boolean }} The period and whether it began.
 */
export function periodOf(
  code: SalesPerformancePeriodCode,
  today: string,
): { period: SalesPerformancePeriod; started: boolean } {
  const year = financialYearOf(today)
  const startYear = Number(year.start.slice(0, 4))
  if (code === 'FY') {
    return {
      period: {
        code,
        label: `FY ${year.label} to date`,
        startDate: year.start,
        endDate: year.end,
      },
      started: true,
    }
  }
  const [yearOffset, startMonth] = QUARTER_STARTS[code] ?? [0, 4]
  const first = new Date(Date.UTC(startYear + yearOffset, startMonth - 1, 1))
  const last = new Date(Date.UTC(startYear + yearOffset, startMonth + 2, 0))
  const startDate = first.toISOString().slice(0, 10)
  return {
    period: {
      code,
      label: `${code} ${year.label}`,
      startDate,
      endDate: last.toISOString().slice(0, 10),
    },
    started: startDate <= today,
  }
}

/**
 * @description Every calendar month of the period that has begun, as YYYY-MM.
 * @param {SalesPerformancePeriod} period - The period.
 * @param {string} today - Today in India, yyyy-mm-dd.
 * @returns {string[]} The months, earliest first.
 */
export function monthsOf(period: SalesPerformancePeriod, today: string): string[] {
  const last = (period.endDate < today ? period.endDate : today).slice(0, 7)
  const months: string[] = []
  const cursor = new Date(`${period.startDate.slice(0, 7)}-01T00:00:00Z`)
  while (cursor.toISOString().slice(0, 7) <= last) {
    months.push(cursor.toISOString().slice(0, 7))
    cursor.setUTCMonth(cursor.getUTCMonth() + 1)
  }
  return months
}

/**
 * @description The targets owned by the given people that count in the period: those whose
 * counting date falls inside it, plus, when asked, open targets that were due before it.
 * @param {Set<string>} ownerIds - The people whose targets to take.
 * @param {SalesPerformancePeriod} period - The period.
 * @param {boolean} includeEarlierOverdue - Whether to add open targets overdue from before it.
 * @returns {PerformanceTarget[]} The targets with their facts.
 */
export function targetsFor(
  ownerIds: Set<string>,
  period: SalesPerformancePeriod,
  includeEarlierOverdue: boolean,
): PerformanceTarget[] {
  const today = indiaToday()
  return listTargets().flatMap((target) => {
    const tenant = findTenantById(target.tenantId)
    if (!tenant || !ownerIds.has(target.ownerId)) return []
    const achieved =
      target.status === 'Completed' ||
      target.stage === 'Awaiting Payment' ||
      target.stage === 'Active Client'
    const date = achieved ? (target.completedAt?.slice(0, 10) ?? target.endDate) : target.endDate
    const inPeriod = date >= period.startDate && date <= period.endDate
    const earlierOverdue = !achieved && target.endDate < period.startDate && target.endDate < today
    if (!inPeriod && !(includeEarlierOverdue && earlierOverdue)) return []
    return [{ target, tenant, achieved, onboarded: target.status === 'Completed', date }]
  })
}

/**
 * @description A percentage of assigned targets that were achieved, or null when none were
 * assigned.
 * @param {number} achieved - Achieved targets.
 * @param {number} assigned - Assigned targets.
 * @returns {number | null} The rounded percentage.
 */
export function percentOf(achieved: number, assigned: number): number | null {
  return assigned === 0 ? null : Math.round((achieved / assigned) * 100)
}

/**
 * @description How an open target is doing on the pending list: On Hold when the prospect is on
 * hold (its clock stops, so it is never Overdue or At Risk), Overdue once due, At Risk when its
 * display status says so, otherwise On Track (an Awaiting Payment target is still pending).
 * @param {TargetWithOverdue} target - An open target.
 * @param {string} today - Today in India, yyyy-mm-dd.
 * @returns {SalesPerformancePendingStatus} The pending status.
 */
export function pendingStatusOf(
  target: TargetWithOverdue,
  today: string,
): SalesPerformancePendingStatus {
  if (target.stage === 'On Hold') return 'ON_HOLD'
  const status = displayStatusOf(target, today)
  if (status === 'OVERDUE' || status === 'AT_RISK') return status
  return 'ON_TRACK'
}

/**
 * @description The ranking order: achievement descending, a tie to the higher role, more assigned,
 * then name; people with nothing assigned follow by joining date, earliest first.
 * @param {{ user: SalesUserRecord; assigned: number; achieved: number }} a - One person.
 * @param {{ user: SalesUserRecord; assigned: number; achieved: number }} b - Another person.
 * @returns {number} Negative when a ranks first.
 */
export function compareRanking(
  a: { user: SalesUserRecord; assigned: number; achieved: number },
  b: { user: SalesUserRecord; assigned: number; achieved: number },
): number {
  if ((a.assigned === 0) !== (b.assigned === 0)) return a.assigned === 0 ? 1 : -1
  if (a.assigned === 0) return a.user.doj.localeCompare(b.user.doj)
  const byPercent = b.achieved / b.assigned - a.achieved / a.assigned
  if (byPercent !== 0) return byPercent
  const byRole = ROLE_ORDER[roleCodeOf(a.user)] - ROLE_ORDER[roleCodeOf(b.user)]
  if (byRole !== 0) return byRole
  return b.assigned - a.assigned || a.user.name.localeCompare(b.user.name)
}
